// /signup: self-serve sign-up. With native sign-in, the portal's own sign-up screen (NativeSignUp) takes work
// email, name, company and a password, and Entra confirms the email with a code. With Microsoft's sign-in pages,
// "Start free" lands here and is sent straight to Microsoft's sign-up page, which asks for a name and a verified
// email; the company is asked next, on the portal's create-workspace screen (SessionProvider → POST /workspace).
// Without Microsoft sign-in, the form below posts company, name and work email to /signup and an invitation does
// the verifying.
import { useEffect, useState } from "react";
import { BrowserCacheLocation, PublicClientApplication } from "@azure/msal-browser";
import { loadAuthConfig, loadConfig, type AuthConfig, type PortalConfig } from "../config";
import { nativeClient, usesNativeSignIn, type NativeClient } from "../auth/native";
import { NativeSignUp } from "./NativeSignUp";
import { Splash } from "../shared/Splash";
import { Banner, CheckIcon, KV, overline } from "../shared/components";
import { Button, Checkbox, Icon, Input, useToast } from "../ui";
import { ToastProvider } from "../ui/toast";

interface Result { tenantId: string; name?: string; intakeAddress?: string; emailed: boolean; inviteUrl: string | null; existing?: boolean }

export function SignUpPage() {
  return <ToastProvider><SignUp /></ToastProvider>;
}

function SignUp() {
  const toast = useToast();
  const [config, setConfig] = useState<{ portal: PortalConfig; auth: AuthConfig } | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [f, setF] = useState({ company: "", name: "", email: "", terms: false });
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Result | null>(null);
  // Until the config says otherwise, assume the Microsoft hand-off, so the form never flashes first.
  const [handingOff, setHandingOff] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [native, setNative] = useState<{ client: NativeClient; scope: string; environment: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const portal = await loadConfig();
        const auth = await loadAuthConfig(portal.apiUrl);
        if (!auth.selfServeSignUp) {
          setHandingOff(false);
          setUnavailable("Sign-up isn’t open here yet. Ask AutoVouch for an invitation.");
        } else if (usesNativeSignIn(auth)) {
          setNative({ client: await nativeClient(portal, auth), scope: auth.scope!, environment: portal.environment });
          setHandingOff(false);
        } else if (auth.enabled && auth.authority && auth.clientId && auth.scope) {
          // Microsoft's page creates the login; prompt=create opens it on the sign-up step.
          const app = new PublicClientApplication({
            auth: { clientId: auth.clientId, authority: auth.authority, knownAuthorities: [new URL(auth.authority).host], redirectUri: window.location.origin, navigateToLoginRequestUrl: false },
            cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
          });
          await app.initialize();
          // Coming back here after an earlier hand-off (Back, then Sign up again) leaves MSAL believing a
          // redirect is still in progress; settling it clears that, or loginRedirect refuses to start.
          await app.handleRedirectPromise();
          await app.loginRedirect({ scopes: [auth.scope], prompt: "create" });
          return;
        } else {
          setHandingOff(false);
          setConfig({ portal, auth });
        }
      } catch (e) {
        setFailed(e instanceof Error ? e.message : String(e));
      }
    })();
    // Restored from the back-forward cache after the hand-off, the page would sit on the splash: start over.
    const restored = (ev: PageTransitionEvent) => { if (ev.persisted) window.location.reload(); };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);

  const problems = {
    name: !f.name.trim() ? "Tell us your name" : "",
    company: !f.company.trim() ? "Which company is this for?" : "",
    email: !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email) ? "Enter a valid work email" : "",
    terms: !f.terms ? "Please accept the terms to continue" : "",
  };
  const ok = !Object.values(problems).some(Boolean);
  const e = (k: keyof typeof problems) => (tried ? errors[k] || problems[k] || undefined : errors[k] || undefined);

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setTried(true);
    if (!ok || !config) return;
    setBusy(true);
    setErrors({});
    try {
      const response = await fetch(`${config.portal.apiUrl}/api/v1/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company: f.company.trim(), name: f.name.trim(), email: f.email.trim(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      const body = (await response.json().catch(() => ({}))) as Result & { title?: string; errors?: Record<string, string[]> };
      if (!response.ok) {
        if (body.errors) setErrors(Object.fromEntries(Object.entries(body.errors).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v.join(" ")])));
        else toast({ tone: "error", title: "Couldn’t sign up", message: body.title ?? `The API answered ${response.status}.` });
        return;
      }
      setResult(body);
    } catch (err) {
      toast({ tone: "error", title: "Couldn’t sign up", message: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  if (failed) return <Splash tone="error" text="Couldn’t open sign-up" detail={failed} action={<Button variant="outline" onClick={() => window.location.reload()}>Try again</Button>} />;
  if (handingOff) return <Splash text="Taking you to sign-up…" />;
  if (native) {
    const site = native.environment === "prod" ? "https://www.autovouch.app" : `https://www.${native.environment}.autovouch.app`;
    return <NativeSignUp native={native.client} scope={native.scope} siteUrl={site} />;
  }

  const siteUrl = config?.portal.environment === "prod" ? "https://www.autovouch.app" : config ? `https://www.${config.portal.environment}.autovouch.app` : "/";
  const points: [string, string][] = result
    ? [["mail", result.emailed ? `We emailed ${f.email.trim()} an invitation` : "Your invitation link is below"], ["log-in", "Open it and sign in with Microsoft using that email"], ["eye", "Your workspace starts in preview mode: nothing changes in Syncore until you go live"]]
    : [["file-search", "Reads any supplier’s invoice layout"], ["list-checks", "Checks every line against the PO"], ["circle-check", "Approves clean matches for you"]];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(320px,.8fr) minmax(0,1.2fr)", minHeight: "100vh", background: "#fff" }}>
      <aside style={{ position: "relative", overflow: "hidden", background: "var(--gradient-glow),var(--gradient-deep)", color: "#fff", padding: "44px 52px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 24 }}>
        <a href={siteUrl} style={{ alignSelf: "flex-start", display: "flex" }}><img src="/logo-wordmark-inverse.png" alt="AutoVouch" style={{ height: 42 }} /></a>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ ...overline, color: "var(--av-cyan-400)" }}>{result ? "One more step" : "Built for Syncore"}</div>
          <h2 style={{ font: "800 clamp(34px,3.4vw,46px)/1.05 var(--font-display)", letterSpacing: "-0.02em", margin: 0, color: "#fff", whiteSpace: "pre-line" }}>{result ? "Check your email." : "Vouched\non arrival."}</h2>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            {points.map(([ic, t]) => <li key={t} style={{ display: "flex", alignItems: "center", gap: 14, font: "600 16px/1.4 var(--font-body)", color: "var(--av-sky)" }}><span style={{ flex: "none", width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--av-cyan-400)" }}><Icon name={ic} size={18} /></span>{t}</li>)}
          </ul>
        </div>
        <div style={{ padding: "20px 22px", borderRadius: 24, background: "rgba(255,255,255,.07)", border: "1px solid var(--border-inverse)", display: "flex", alignItems: "center", gap: 18 }}>
          <span style={{ font: "900 44px/1 var(--font-display)", letterSpacing: "-0.03em", background: "var(--gradient-fresh)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", filter: "brightness(1.35)" }}>10</span>
          <div style={{ font: "14px/1.45 var(--font-body)", color: "var(--av-sky)" }}>approved invoices free. No card needed. Then plans from $199/mo for 500 invoices.</div>
        </div>
      </aside>
      <main style={{ display: "flex", flexDirection: "column", padding: "28px clamp(24px,5vw,64px)" }}>
        <header style={{ display: "flex", alignItems: "center", gap: 16, minHeight: 40 }}>
          <span style={{ flex: 1 }} />
          <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Have an account? <a href="/">Sign in</a></span>
        </header>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 0" }}>
          <div style={{ width: "100%", maxWidth: 460, display: "flex", flexDirection: "column", gap: 28, animation: "av-rise var(--dur-slow) var(--ease-out)" }}>
            {unavailable ? (
              <><h1 style={{ font: "800 34px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>Sign-up isn’t open yet</h1><Banner tone="info" icon="info" title="By invitation for now" text={unavailable} /><a href={siteUrl}>Back to the site</a></>
            ) : result ? (
              <>
                <div><h1 style={{ font: "800 34px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>{result.existing ? "You already have a workspace" : "Your workspace is ready"}</h1><p style={{ font: "17px/1.5 var(--font-body)", color: "var(--text-muted)", margin: "10px 0 0" }}>{result.emailed ? `We sent an invitation to ${f.email.trim()}. Open it and sign in with Microsoft using that address; it lasts 7 days.` : "Email isn’t set up on this environment, so here is your invitation link. Open it and sign in with Microsoft using the address you gave."}</p></div>
                <KV columns="150px 1fr" rows={[["Workspace", result.name ?? result.tenantId], ...(result.intakeAddress ? [["Forwarding address", <span key="a" style={{ wordBreak: "break-all" }}>{result.intakeAddress}</span>] as [React.ReactNode, React.ReactNode]] : []), ["Mode", "Preview – nothing changes in Syncore until you go live"]]} />
                {result.inviteUrl && <div style={{ display: "flex", gap: 8 }}><Input readOnly value={result.inviteUrl} containerStyle={{ flex: 1 }} mono onFocus={(ev) => ev.currentTarget.select()} /><Button leadingIcon={<Icon name="arrow-right" size={16} />} onClick={() => window.location.assign(result.inviteUrl!)}>Open</Button></div>}
                <span style={{ display: "flex", alignItems: "center", gap: 8, font: "600 13px var(--font-body)", color: "var(--status-matched-fg)" }}><CheckIcon r="ok" size={16} />The first ten approved invoices are free</span>
              </>
            ) : (
              <>
                <div><h1 style={{ font: "800 34px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>Create your account</h1><p style={{ font: "17px/1.5 var(--font-body)", color: "var(--text-muted)", margin: "10px 0 0" }}>Tell us about your company. You’ll create your login with Microsoft next.</p></div>
                <form noValidate onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <Input label="Full name" placeholder="Jess Lin" autoComplete="name" value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} error={e("name")} />
                    <Input label="Company" placeholder="Northwind Promo" autoComplete="organization" value={f.company} onChange={(ev) => setF({ ...f, company: ev.target.value })} error={e("company")} />
                  </div>
                  <Input label="Work email" type="email" placeholder="jess@northwindpromo.com" autoComplete="email" leadingIcon={<Icon name="mail" size={17} />} value={f.email} onChange={(ev) => setF({ ...f, email: ev.target.value })} error={e("email")} />
                  <Checkbox checked={f.terms} onChange={(v) => setF({ ...f, terms: v })} label={<span>I agree to the <a href={siteUrl + "/terms"} target="_blank" rel="noreferrer">Terms of Service</a> and <a href={siteUrl + "/privacy"} target="_blank" rel="noreferrer">Privacy Policy</a></span>} />
                  {e("terms") && <div style={{ display: "flex", alignItems: "center", gap: 6, font: "var(--type-body-sm)", color: "var(--status-mismatch-fg)" }}><Icon name="circle-alert" size={15} />{e("terms")}</div>}
                  <Button type="submit" size="lg" fullWidth loading={busy || !config} trailingIcon={<Icon name="arrow-right" size={18} />} style={{ marginTop: 6 }}>Continue</Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
